/**
 * usePretext — Hook for Pretext text measurement in chat bubbles.
 *
 * Uses @chenglou/pretext to pre-calculate message heights without DOM reflow.
 * This enables true virtualization: we know every message's pixel height before
 * it's ever rendered, so the virtual list can position items perfectly.
 *
 * Key Pretext APIs used:
 * - prepare(text, font, { whiteSpace }) → PreparedText (pre-processes text for layout)
 * - layout(prepared, maxWidth, lineHeight) → { lineCount, height }
 *
 * height = lineCount * lineHeight — Pretext only measures the TEXT. Everything
 * else (padding, spacing, sender names, replies, timestamps, reactions, date
 * separators) must be added manually by matching the exact CSS pixel values
 * from message-bubble.tsx.
 *
 * A ResizeObserver-based correction layer in the virtual list handles edge
 * cases where the estimate doesn't match DOM reality.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { prepare, layout, type PreparedText } from "@chenglou/pretext";
import { getCurrentUserId } from "../stores/messenger-store";
import type { Message } from "../types";

// ─── Emoji-only detection (shared with message-bubble.tsx) ──────────────────

/** Regex matching one or more emoji characters with optional skin tones/ZWJ sequences */
const EMOJI_ONLY_REGEX = new RegExp(
  "^(?:\\p{Emoji_Presentation}|\\p{Emoji}\\uFE0F)" +
    "(?:\\u200D(?:\\p{Emoji_Presentation}|\\p{Emoji}\\uFE0F))*" +
    "(?:\\s*(?:\\p{Emoji_Presentation}|\\p{Emoji}\\uFE0F)" +
    "(?:\\u200D(?:\\p{Emoji_Presentation}|\\p{Emoji}\\uFE0F))*)*$",
  "u"
);

/** Check if a message is only emojis (up to ~5). Used for jumbo emoji display. */
export function isEmojiOnly(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > 20) return false;
  return EMOJI_ONLY_REGEX.test(trimmed);
}

/**
 * Two consecutive messages belong to the same "burst" when the same person
 * sent them close together with nothing between. Bursts render as one block:
 * one avatar, one name, one timestamp, and a hairline gap between lines.
 */
const BURST_WINDOW_MS = 7 * 60_000;

// ─── Constants matching our bubble CSS ──────────────────────────────────────
// These MUST precisely match the Tailwind classes in message-bubble.tsx.
// Any mismatch causes height estimation errors → overlap / gaps.
//
// Methodology: every Tailwind class was manually converted to pixel values
// using 1rem = 16px (browser default). Line-heights are pinned with explicit
// `leading-[21px]` classes in the bubble so there is nothing to guess.

/**
 * Message body type. Phones get one step larger — 14px is a squint on a
 * handset — and the estimator has to use the same numbers the CSS does or
 * every height is wrong. `useIsNarrowViewport` below reads the exact same
 * 640px breakpoint Tailwind's `sm:` uses.
 */
export const BUBBLE_FONT = '14px "Inter", system-ui, sans-serif';
export const BUBBLE_FONT_NARROW = '15px "Inter", system-ui, sans-serif';

/** Explicit `leading-[21px]` / `leading-[22px]` on message text. */
export const BUBBLE_LINE_HEIGHT = 21;
export const BUBBLE_LINE_HEIGHT_NARROW = 22;

/** Matches Tailwind's `sm:` breakpoint so JS metrics and CSS never disagree. */
export function useIsNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = window.matchMedia("(max-width: 639px)");
    const onChange = () => setNarrow(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

/** Horizontal padding inside bubble: px-3.5 = 0.875rem = 14px each side */
const BUBBLE_PADDING_H = 14;

/** Vertical padding inside bubble: py-2 = 0.5rem = 8px each side → 16px total */
const BUBBLE_PADDING_V = 8;

/** Widest a bubble ever gets, regardless of window width. Long lines hurt. */
const BUBBLE_MAX_WIDTH_CAP = 560;

/** Avatar gutter beside non-own bubbles in group chats: w-8 (32) + gap-2 (8) */
const BUBBLE_AVATAR_GUTTER = 40;

/**
 * Sender name row (bubbles layout, group chats, burst head only):
 *   <p className="mb-0.5 ml-1 text-[12.5px] font-semibold leading-4">
 *   leading-4 = 16px + mb-0.5 = 2px → 18px
 */
const SENDER_NAME_HEIGHT = 18;

/**
 * Reply preview above a bubble:
 *   <button className="mb-1 flex h-6 items-center gap-1.5 rounded-lg px-2 text-[11px]">
 *   h-6 = 24px + mb-1 = 4px → 28px
 */
const REPLY_PREVIEW_HEIGHT = 28;

/**
 * Timestamp row (bubbles layout — burst tail only):
 *   <div className="mt-1 flex h-[13px] items-center gap-1 px-1 text-[10px]">
 *   13px + mt-1 (4px) = 17px
 */
const TIMESTAMP_HEIGHT = 17;

/**
 * Reactions row:
 *   <div className="mt-1 flex flex-wrap gap-1">
 *   button: h-6 = 24px, mt-1 = 4px → 28px
 */
const REACTION_ROW_HEIGHT = 28;

/**
 * Pinned marker above a bubble: h-[13px] + mb-0.5 (2px) = 15px
 */
const PINNED_BADGE_HEIGHT = 15;

/**
 * Date separator (rendered by virtual-message-list.tsx):
 *   <div className="flex items-center justify-center py-3.5">  → 28px
 *     <span className="py-1 text-[10px]">                      → 8 + 13 = 21px
 */
export const DATE_SEPARATOR_HEIGHT = 49;

/**
 * System message:
 *   <div className="flex justify-center py-1.5">      → 12px
 *     <span className="px-3 py-1 text-[11px]">        → 8 + 15 = 23px
 */
const SYSTEM_MESSAGE_HEIGHT = 35;

// ─── Vertical rhythm ────────────────────────────────────────────────────────
// Two gaps, not one. Messages from the same person in the same burst sit
// almost flush (Discord-style); a new speaker gets real air. The virtual list
// bakes these into absolute `top` values, so the DOM elements themselves carry
// no margins — `messageSpacing()` is the single source of truth for both the
// height estimate and the ResizeObserver correction.

/** Gap above the first message of a new burst — bubbles layout. */
const GROUP_GAP_BUBBLES = 14;
/** Gap above a continuation message from the same person — bubbles layout. */
const STACK_GAP_BUBBLES = 3;
/** Gap above the first message of a new burst — compact layout. */
const GROUP_GAP_COMPACT = 14;
/**
 * Continuation lines in compact layout get no extra gap: the row's own
 * py-[2px] padding on both neighbours already leaves 4px, which is exactly
 * the Discord rhythm.
 */
const STACK_GAP_COMPACT = 0;

/**
 * Gap between a message and the one after it, chosen by how the *next*
 * message relates to this one. The virtual list positions items by absolute
 * `top` with no CSS margins, so each item's slot has to carry the gap that
 * follows it — carrying the gap that precedes it instead put the wide
 * speaker-change gap inside bursts and the tight one between them.
 */
export function trailingSpacing(
  layoutMode: "bubbles" | "irc",
  nextIsGrouped: boolean
): number {
  if (layoutMode === "irc") {
    return nextIsGrouped ? STACK_GAP_COMPACT : GROUP_GAP_COMPACT;
  }
  return nextIsGrouped ? STACK_GAP_BUBBLES : GROUP_GAP_BUBBLES;
}

/**
 * Compact row vertical padding: py-[2px] = 2px each side = 4px total.
 * Inside the DOM element, so ResizeObserver measures it.
 */
const COMPACT_ROW_PADDING_V = 4;

/**
 * Image/GIF max dimensions — these MUST match the constants in message-bubble.tsx.
 */
const IMAGE_MAX_WIDTH = 320;
const IMAGE_MAX_HEIGHT = 256;

/** Image/GIF bubble padding: p-0.5 = 2px each side → 4px total */
const IMAGE_BUBBLE_PADDING = 4;

/**
 * File attachment inside a bubble: h-9 icon (36px) + p-2 (16px) = 52px.
 */
const FILE_CONTENT_HEIGHT = 52;

/**
 * Voice note player (components/voice-note.tsx): h-12 (48px) + mt-1 (4px).
 * Previously missing entirely, which let voice messages overlap the message
 * below until the ResizeObserver caught up.
 */
const VOICE_CONTENT_HEIGHT = 52;

/** "Encrypted message — decrypting…" placeholder line. */
const ENCRYPTED_PLACEHOLDER_HEIGHT = 18;

/**
 * Game invite card (components/game-invite-card.tsx) and inline game panel
 * (components/inline-game.tsx). Both render at a FIXED height precisely so
 * these estimates are exact — an inline game whose height changed as it loaded
 * would shove every message below it around mid-render. Mirrors
 * INVITE_CARD_HEIGHT / INLINE_GAME_HEIGHT; change the pairs together.
 */
const GAME_INVITE_CARD_HEIGHT = 128;
const INLINE_GAME_HEIGHT = 470;

/**
 * Link-preview card rendered inline under text content:
 *   mt-2 (8px) + h-[300px] card = 308px
 *
 * Only added when the message has `content.linkPreview` saved, so we know
 * up-front a card will render. URLs that resolve via the on-mount fetch fall
 * back to ResizeObserver correction.
 */
const LINK_PREVIEW_HEIGHT = 308;

/**
 * Emoji-only message: text-4xl (36px) at leading-tight (1.25) ≈ 45px, and the
 * bubble drops its padding entirely.
 */
const EMOJI_MESSAGE_HEIGHT = 45;

// ─── Compact layout constants ───────────────────────────────────────────────
// Compact layout: avatar column (40px + 12px gutter) | content column.
// Continuation rows hide the avatar and header and show just indented text.

/**
 * Compact header row (burst head only):
 *   <div className="mb-0.5 flex items-baseline gap-2">
 *     <span className="text-[15px] font-semibold leading-5">name</span>
 *   leading-5 = 20px + mb-0.5 = 2px → 22px
 */
const COMPACT_HEADER_HEIGHT = 22;

/**
 * Compact reply line above the content: h-[18px] + mb-0.5 (2px) = 20px.
 * Previously unaccounted for.
 */
const COMPACT_REPLY_HEIGHT = 20;

/**
 * Avatar column: h-10 (40px) + pt-0.5 (2px) = 42px. With items-start the row's
 * inner height is max(avatar column, content column).
 */
const COMPACT_AVATAR_COL_HEIGHT = 42;

const COMPACT_AVATAR_COL_WIDTH = 40 + 12; // w-10 + mr-3
const COMPACT_OUTER_PADDING_H = 16; // px-4 each side

/** Compact emoji: text-3xl leading-normal = 30px * 1.5 = 45px */
const COMPACT_EMOJI_HEIGHT = 45;

/** Compact file chip: mt-1 (4px) + py-2 (16px) + 20px text line = 40px */
const COMPACT_FILE_HEIGHT = 40;

/** Compact image/gif: mt-1.5 (6px) wrapper + image height */
const COMPACT_MEDIA_MARGIN_TOP = 6;

/** Compact reactions row: mt-1 (4px) + h-6 button (24px) = 28px */
const COMPACT_REACTION_ROW_HEIGHT = 28;

// ─── Prepared text cache ────────────────────────────────────────────────────

/**
 * Cache for prepared text objects. Keyed by message ID since message content
 * doesn't change (edits create new updatedAt which triggers recalc).
 *
 * IMPORTANT: We pass { whiteSpace: 'pre-wrap' } to match CSS `whitespace-pre-wrap`
 * on the <p> element in FormattedText. Without this, Pretext treats whitespace
 * in 'normal' mode (collapsing newlines), causing line count mismatches.
 */
const preparedCache = new Map<
  string,
  { prepared: PreparedText; updatedAt: string | number; font: string; text: string }
>();

function getPreparedText(
  messageId: string,
  text: string,
  updatedAt: string | number,
  font: string
): PreparedText {
  const cached = preparedCache.get(messageId);
  if (cached && cached.updatedAt === updatedAt && cached.font === font && cached.text === text) {
    return cached.prepared;
  }
  const prepared = prepare(text, font, { whiteSpace: "pre-wrap" });
  if (preparedCache.size >= 2000) preparedCache.delete(preparedCache.keys().next().value!);
  preparedCache.set(messageId, { prepared, updatedAt, font, text });
  return prepared;
}

/** Clear stale entries from cache (call periodically or on conversation switch) */
export function clearPretextCache(): void {
  preparedCache.clear();
}

// ─── Font readiness ─────────────────────────────────────────────────────────

/**
 * Hook that returns true once the Inter font (or fallback) is loaded.
 * Pretext measurements are inaccurate until the font is available because
 * character widths differ between fonts → different line counts.
 */
export function useFontReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(() => {
        if (!cancelled) setReady(true);
      });
    } else {
      setReady(true);
    }
    return () => {
      cancelled = true;
    };
  }, []);
  return ready;
}

// ─── Height calculation ─────────────────────────────────────────────────────

export interface MessageHeightParams {
  /** Max width available for a bubble with no avatar gutter beside it. */
  bubbleMaxWidth: number;
  /** Max width for a bubble that sits right of the group-chat avatar gutter. */
  bubbleMaxWidthWithAvatar: number;
  /** Full container width (used for compact layout width calculation) */
  containerWidth: number;
  /** Whether this is a group chat (affects sender name + avatar display) */
  isGroup: boolean;
  /** Layout mode — "bubbles" (default) or "irc" (compact) */
  layoutMode: "bubbles" | "irc";
  /** Id of the signed-in user — decides which side a message renders on. */
  currentUserId: string;
  /** Body font string, chosen by viewport width. */
  font: string;
  /** Body line-height in px, matching `font`. */
  lineHeight: number;
}

/** Where a message sits inside its burst, plus whether a day changed above it. */
export interface MessagePosition {
  showDateSeparator: boolean;
  /** True when the message continues a burst from the same sender. */
  isGrouped: boolean;
  /** True when nothing below continues this burst — the burst's last line. */
  isGroupTail: boolean;
}

function sameDay(a: Message["createdAt"], b: Message["createdAt"]): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** Does `message` continue a burst started by `prevMessage`? */
function continuesBurst(
  message: Message | undefined,
  prevMessage: Message | undefined
): boolean {
  if (!message || !prevMessage) return false;
  if (prevMessage.senderId !== message.senderId) return false;
  if (prevMessage.content.type === "system") return false;
  if (message.content.type === "system") return false;
  if (!sameDay(message.createdAt, prevMessage.createdAt)) return false;
  return (
    new Date(message.createdAt).getTime() -
      new Date(prevMessage.createdAt).getTime() <
    BURST_WINDOW_MS
  );
}

/** Resolve burst position + date-separator flag for one index in a list. */
export function getMessagePosition(
  messages: Message[],
  index: number
): MessagePosition {
  const message = messages[index];
  const prevMessage = index > 0 ? messages[index - 1] : undefined;
  const nextMessage = messages[index + 1];

  const showDateSeparator =
    !prevMessage || !sameDay(message.createdAt, prevMessage.createdAt);

  const isGrouped = !showDateSeparator && continuesBurst(message, prevMessage);
  const nextShowsDateSeparator =
    !!nextMessage && !sameDay(nextMessage.createdAt, message.createdAt);
  const isGroupTail =
    !nextMessage ||
    nextShowsDateSeparator ||
    !continuesBurst(nextMessage, message);

  return { showDateSeparator, isGrouped, isGroupTail };
}

/**
 * Pixel height of everything a message item actually renders — its optional
 * date separator plus its content. The gap that follows it is added
 * separately by `useMessageLayouts` (see `trailingSpacing`).
 */
export function calculateMessageHeight(
  message: Message,
  position: MessagePosition,
  params: MessageHeightParams
): number {
  let height = 0;

  if (position.showDateSeparator) {
    height += DATE_SEPARATOR_HEIGHT;
  }

  // System messages — centred chip, no bubble/timestamp/reactions.
  if (message.content.type === "system") {
    return height + SYSTEM_MESSAGE_HEIGHT;
  }

  if (params.layoutMode === "irc") {
    height += calculateCompactContentHeight(message, position, params);
  } else {
    height += calculateBubbleContentHeight(message, position, params);
  }

  return height;
}

/** Height of the message body itself, measured at a given wrap width. */
function measureBodyHeight(
  message: Message,
  contentWidth: number,
  lineHeight: number,
  emojiHeight: number,
  font: string
): number {
  switch (message.content.type) {
    case "text": {
      const emojiOnly =
        !message.content.formatting?.length && isEmojiOnly(message.content.text);
      if (emojiOnly) return emojiHeight;
      const prepared = getPreparedText(
        message.id,
        message.content.text,
        message.updatedAt,
        font
      );
      return layout(prepared, Math.max(40, contentWidth), lineHeight).height;
    }
    case "image":
    case "gif": {
      const scale = Math.min(
        1,
        IMAGE_MAX_WIDTH / message.content.width,
        IMAGE_MAX_HEIGHT / message.content.height
      );
      return Math.round(message.content.height * scale);
    }
    case "file":
      return FILE_CONTENT_HEIGHT;
    case "voice":
      return VOICE_CONTENT_HEIGHT;
    case "game_invite":
      return GAME_INVITE_CARD_HEIGHT;
    case "game_inline":
      return INLINE_GAME_HEIGHT;
    case "encrypted":
      return ENCRYPTED_PLACEHOLDER_HEIGHT;
    default:
      return lineHeight;
  }
}

/**
 * Compact (Discord-like) layout:
 *   <div class="flex items-start px-4 py-[2px]">
 *     <div class="mr-3 w-10 pt-0.5">avatar or hover-timestamp</div>
 *     <div class="flex-1">[reply] [header] [content] [reactions]</div>
 *   </div>
 */
function calculateCompactContentHeight(
  message: Message,
  position: MessagePosition,
  params: MessageHeightParams
): number {
  let contentColHeight = 0;

  const compactContentWidth =
    params.containerWidth -
    COMPACT_AVATAR_COL_WIDTH -
    COMPACT_OUTER_PADDING_H * 2;

  if (message.replyToId) contentColHeight += COMPACT_REPLY_HEIGHT;
  if (!position.isGrouped) contentColHeight += COMPACT_HEADER_HEIGHT;

  // Game cards, like media, sit below the header with their own top margin
  // instead of flowing as text.
  const isMedia =
    message.content.type === "image" ||
    message.content.type === "gif" ||
    message.content.type === "game_invite" ||
    message.content.type === "game_inline";
  contentColHeight +=
    (isMedia ? COMPACT_MEDIA_MARGIN_TOP : 0) +
    measureBodyHeight(
      message,
      compactContentWidth,
      params.lineHeight,
      COMPACT_EMOJI_HEIGHT,
      params.font
    );

  if (message.content.type === "file") {
    // Compact renders the file chip inline (no bubble padding around it).
    contentColHeight += COMPACT_FILE_HEIGHT - FILE_CONTENT_HEIGHT;
  }
  if (message.content.type === "text" && message.content.linkPreview) {
    contentColHeight += LINK_PREVIEW_HEIGHT;
  }
  if (message.reactions.length > 0) {
    contentColHeight += COMPACT_REACTION_ROW_HEIGHT;
  }

  // Burst heads must clear the 40px avatar; continuation rows have no avatar.
  const innerHeight = position.isGrouped
    ? contentColHeight
    : Math.max(contentColHeight, COMPACT_AVATAR_COL_HEIGHT);

  return innerHeight + COMPACT_ROW_PADDING_V;
}

/**
 * Bubbles layout: [avatar gutter] name → reply → bubble → timestamp → reactions.
 * Name shows on burst heads in group chats; timestamp shows on burst tails.
 */
function calculateBubbleContentHeight(
  message: Message,
  position: MessagePosition,
  params: MessageHeightParams
): number {
  let height = 0;
  const isOwn = message.senderId === params.currentUserId;
  const showsAvatarGutter = params.isGroup && !isOwn;

  if (showsAvatarGutter && !position.isGrouped) {
    height += SENDER_NAME_HEIGHT;
  }
  if (message.replyToId) {
    height += REPLY_PREVIEW_HEIGHT;
  }
  if (message.isPinned) {
    height += PINNED_BADGE_HEIGHT;
  }

  const bubbleWidth = showsAvatarGutter
    ? params.bubbleMaxWidthWithAvatar
    : params.bubbleMaxWidth;
  const contentMaxWidth = bubbleWidth - BUBBLE_PADDING_H * 2;

  const bodyHeight = measureBodyHeight(
    message,
    contentMaxWidth,
    params.lineHeight,
    EMOJI_MESSAGE_HEIGHT,
    params.font
  );

  const isMedia =
    message.content.type === "image" || message.content.type === "gif";
  const isEmojiOnlyText =
    message.content.type === "text" &&
    !message.content.formatting?.length &&
    isEmojiOnly(message.content.text);
  // Game cards bring their own border and padding, so they render outside the
  // bubble shell — no bubble padding to account for.
  const isGameCard =
    message.content.type === "game_invite" || message.content.type === "game_inline";

  if (isMedia) {
    height += bodyHeight + IMAGE_BUBBLE_PADDING;
  } else if (isEmojiOnlyText || isGameCard) {
    height += bodyHeight;
  } else {
    height += bodyHeight + BUBBLE_PADDING_V * 2;
  }

  if (message.content.type === "text" && message.content.linkPreview) {
    height += LINK_PREVIEW_HEIGHT;
  }
  if (position.isGroupTail) {
    height += TIMESTAMP_HEIGHT;
  }
  if (message.reactions.length > 0) {
    height += REACTION_ROW_HEIGHT;
  }

  return height;
}

// ─── Hook: batch height calculation for a message list ──────────────────────

export interface MessageLayout {
  /** Offset from top of the virtual list */
  top: number;
  /** Pretext-estimated height of this message item */
  height: number;
  /** The message */
  message: Message;
  /** Index in the original messages array */
  index: number;
  /** Whether date separator should show above this message */
  showDateSeparator: boolean;
  /** Whether this message continues a burst from the same sender */
  isGrouped: boolean;
  /** Whether this message ends its burst (last line from that sender) */
  isGroupTail: boolean;
  /** Blank space between this item's content and the next item's top. */
  trailingSpacing: number;
}

/**
 * useMessageLayouts — Computes layout positions for all messages using Pretext.
 *
 * Returns a stable array of MessageLayout objects with pre-calculated positions,
 * enabling the virtual list to render only visible items without measuring DOM.
 *
 * The virtual list uses a ResizeObserver to correct any height mismatches.
 */
export function useMessageLayouts(
  messages: Message[],
  containerWidth: number,
  isGroup: boolean,
  layoutMode: "bubbles" | "irc" = "bubbles",
  narrow = false
): { layouts: MessageLayout[]; totalHeight: number } {
  const cacheRef = useRef<{
    messages: Message[];
    containerWidth: number;
    isGroup: boolean;
    layoutMode: "bubbles" | "irc";
    currentUserId: string;
    narrow: boolean;
    result: { layouts: MessageLayout[]; totalHeight: number };
  } | null>(null);

  const currentUserId = getCurrentUserId();

  return useMemo(() => {
    const cache = cacheRef.current;
    if (
      cache &&
      cache.messages === messages &&
      cache.containerWidth === containerWidth &&
      cache.isGroup === isGroup &&
      cache.layoutMode === layoutMode &&
      cache.currentUserId === currentUserId &&
      cache.narrow === narrow
    ) {
      return cache.result;
    }

    const bubbleMaxWidth = Math.min(
      BUBBLE_MAX_WIDTH_CAP,
      Math.floor(containerWidth * 0.76)
    );
    const params: MessageHeightParams = {
      bubbleMaxWidth,
      // The gutter sits *inside* the capped column, so the bubble gets the
      // column width minus the gutter — not 76% of a narrowed container.
      bubbleMaxWidthWithAvatar: bubbleMaxWidth - BUBBLE_AVATAR_GUTTER,
      containerWidth,
      isGroup,
      layoutMode,
      currentUserId,
      font: narrow ? BUBBLE_FONT_NARROW : BUBBLE_FONT,
      lineHeight: narrow ? BUBBLE_LINE_HEIGHT_NARROW : BUBBLE_LINE_HEIGHT,
    };

    const positions = messages.map((_, i) => getMessagePosition(messages, i));

    const layouts: MessageLayout[] = [];
    let currentTop = 0;

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      const position = positions[i];
      // The last item needs no trailing gap — the scroller's own padding
      // already sits under it.
      const trailing =
        i + 1 < messages.length
          ? trailingSpacing(layoutMode, positions[i + 1].isGrouped)
          : 0;
      const height =
        calculateMessageHeight(message, position, params) + trailing;

      layouts.push({
        top: currentTop,
        height,
        message,
        index: i,
        showDateSeparator: position.showDateSeparator,
        isGrouped: position.isGrouped,
        isGroupTail: position.isGroupTail,
        trailingSpacing: trailing,
      });

      currentTop += height;
    }

    const result = { layouts, totalHeight: currentTop };
    cacheRef.current = {
      messages,
      containerWidth,
      isGroup,
      layoutMode,
      currentUserId,
      narrow,
      result,
    };
    return result;
  }, [messages, containerWidth, isGroup, layoutMode, currentUserId, narrow]);
}

// ─── Hook: get visible range from scroll position ───────────────────────────

/**
 * Given sorted layouts and a scroll window, returns the indices of visible items.
 * Uses binary search for O(log n) performance.
 */
export function getVisibleRange(
  layouts: MessageLayout[],
  scrollTop: number,
  viewportHeight: number,
  overscan: number = 5
): { startIndex: number; endIndex: number } {
  if (layouts.length === 0) return { startIndex: 0, endIndex: 0 };

  const viewTop = scrollTop;
  const viewBottom = scrollTop + viewportHeight;

  // Binary search for first visible item
  let lo = 0;
  let hi = layouts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (layouts[mid].top + layouts[mid].height < viewTop) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  const startIndex = Math.max(0, lo - overscan);

  // Binary search for last visible item
  lo = startIndex;
  hi = layouts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (layouts[mid].top > viewBottom) {
      hi = mid - 1;
    } else {
      lo = mid;
    }
  }
  const endIndex = Math.min(layouts.length - 1, lo + overscan);

  return { startIndex, endIndex };
}
